<template>
  <el-dialog
    title="Sign in to a new instance"
    width="30%"
    v-model="visible"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false">
    <el-row type="flex" align="middle" v-loading="isAuthenticating" element-loading-text='Authenticating...'>
      <el-col>
        <el-row type="flex" align="middle">
          <el-form :model="form" ref="authForm">
            <el-form-item style="margin-bottom: 6px;">
              <el-input v-model.trim="form.instanceUrl" auto-complete="off">
                <template v-slot:prepend>Instance URL </template>
                <!-- <template slot="append">{{ instanceServer }}</template> -->
              </el-input>
            </el-form-item>
            <el-form-item style="margin-bottom: 6px;">
              <el-input placeholder="e.g. johndoe" v-model="form.username" :disabled="!form.instanceUrl" ref="usernameField">
                <template v-slot:prepend>Username</template>
              </el-input>
            </el-form-item>
            <el-form-item style="margin-bottom: 6px;">
              <el-input placeholder="********" type="password" v-model="form.password" :disabled="!form.instanceUrl">
                <template v-slot:prepend>Password</template>
              </el-input>
            </el-form-item>
          </el-form>
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="error" type="flex" align="middle" justify="center" style="padding-top: 12px;">
      <el-tag type="danger">{{ error }}</el-tag>
    </el-row>
    <template v-slot:footer><span class="dialog-footer">
      <el-button @click="cancel" class="auth-btn" :disabled="isAuthenticating">
        Cancel
      </el-button>
      <el-button @click="submit" class="auth-btn" :disabled="!hasCredentials || isAuthenticating" type="primary">
        OK
      </el-button>
    </span></template>
  </el-dialog>
</template>

<script>
  import { mapGetters } from 'vuex'
  import isUrl from 'is-url'
  import { tabThruModalButtons, submitOnEnter } from '../utils/helpers/KeyNavHelper'
  import { doesSiteExist } from '@/utils/helpers/SigmacloudRetiring'

  export default {
    name: 'reAuthModal',
    props: { url: String },
    data () {
      return {
        form: { instanceUrl: '', username: '', password: '' },
        formLabelWidth: '120px',
        bindEnterToSubmit: false,
        showErrMsg: false,
        isAuthenticating: false
      }
    },
    methods: {
      /**
       * Resets the form
       */
      resetForm () {
        this.form = Object.assign({}, {
          instanceUrl: '',
          username: '',
          password: ''
        })
      },
      /**
       * Cancels the reauthentication process. Resets the form, the closes the modal. If the user must
       * reauthenticate (e.g. due to an expired token), but hits 'Cancel', then they will be logged
       * out and returned to the login page.
       */
      async cancel () {
        try {
          this.resetForm()
          this.$store.dispatch('changeModalVisibility', { entityType: 'authModal', visible: false })
        } catch (e) {
          console.log(e)
        }
      },
      /**
       * Submits the authentication request for the new instance, then closes the modal.
       */
      async submit () {
        this.showErrMsg = false
        if (this.hasCredentials) {
          this.isAuthenticating = true
          try {
            let instanceUrl
            let exp = new RegExp(`^(https:\\/\\/)?([a-zA-Z0-9_-]+)(.pa.revolutiones.com//)?\\/?$`, 'i')
            if (this.form.instanceUrl.includes('127.0.0') || this.form.instanceUrl.includes('localhost') || this.form.instanceUrl.includes('https') || this.form.instanceUrl.includes('http')) {
              instanceUrl = this.form.instanceUrl
              if (!await doesSiteExist(instanceUrl)) {
                this.$message({ message: 'The instance URL you provided does not appear to be exist. Please check the URL and try again.', type: 'error', duration: 10000 })
                return
              }
            } else {
              instanceUrl = this.form.instanceUrl.match('.pa.revolutiones.com/') ? this.form.instanceUrl : (this.form.instanceUrl.match('https://') ? '' : 'https://') + this.form.instanceUrl.replace(exp, '$2') + '.pa.revolutiones.com/'
              if (instanceUrl[instanceUrl.length - 1] !== '/') instanceUrl = instanceUrl + '/'
              if (!await doesSiteExist(instanceUrl)) {
                instanceUrl = 'https://' + this.form.instanceUrl.replace(exp, '$2') + '.pa.probooks.revolutiones.org/'
                if (instanceUrl[instanceUrl.length - 1] !== '/') instanceUrl = instanceUrl + '/'
                if (!await doesSiteExist(instanceUrl)) {
                  this.$message({ message: 'The instance URL you provided does not appear to be exist. Please check the URL and try again.', type: 'error', duration: 10000 })
                  return
                }
              }
            }
            const url = await this.$store.dispatch('addInstance', instanceUrl, this.form.instanceUrl.replace(exp, '$2'))
            const instance = this.instances.find(instance => instance.url === url)
            if (!instance) {
              throw new Error('Error adding target instance')
            }
            const response = await this.$store.dispatch('authenticate', {
              username: this.form.username,
              password: this.form.password,
              url: instanceUrl
            })
            if (!(response && response.token)) {
              throw new Error('No token received')
            }
            this.resetForm()
            this.$store.dispatch('changeModalVisibility', { entityType: 'authModal', visible: false })
            this.$emit('authSuccess', url)
            return response
          } catch (e) {
            console.log(e)
          } finally {
            this.isAuthenticating = false
          }
        } else {
          this.showErrMsg = true
        }
      }
    },
    computed: {
      ...mapGetters({ getUser: 'getUser', error: 'getAuthFailure', instances: 'getInstances', instanceServer: 'getInstanceServer' }),
      /**
       * Returns the visibility state of the modal
       */
      visible () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.authModal && typeof visible.authModal === 'object' && visible.authModal.visible) {
          return visible.authModal.visible
        }
        return false
      },
      /**
       * Checks whether credentials have been provided
       */
      hasCredentials () {
        return !!(this.form.instanceUrl && this.form.username && this.form.password)
      },
      /**
       * Determines whether the instance URL provided is valid
       */
      validInstanceUrl () {
        return this.form.instanceUrl.includes('localhost') || isUrl(this.form.instanceUrl)
      }
    },
    watch: {
      visible () {
        if (this.visible) {
          tabThruModalButtons('auth-btn')
        } else {
          tabThruModalButtons()
        }
      },
      bindEnterToSubmit () {
        if (this.bindEnterToSubmit && this.$refs.authForm.$el) {
          submitOnEnter(this.$refs.authForm.$el, this.submit)
        } else {
          submitOnEnter()
        }
      }
    },
    created () {
      this.form.username = this.getUser.username
    },
    updated () {
      /*
        In case someone is wondering why we set this.bindEnterToSubmit in
        Vue's updated hook and then watch it in order to use Mousetrap to
        bind the enter key to the submit function, it's because Element
        renders the content inside <el-dialog> *after* its "visible" prop is
        set to true, except for the buttons in the modal footer. So, trying to
        achieve the same thing within the 'visible' watcher (the way we bind tab
        to the modal footer buttons) won't work, because, at that point in time,
        the content within <el-dialog> isn't rendered in the DOM yet, so Mousetrap
        can't find the form to which we're binding the enter key. We could, of
        course, put the logic contained in the bindEnterToSubmit watcher right
        here, but then it would fire on every keystroke...
       */
      this.bindEnterToSubmit = !!this.visible
    }
  }
</script>

<style scoped>
  #err-msg {
    margin-top: 6px;
    color: red;
  }
</style>
